import React, { useState, useRef, ElementType, useEffect } from 'react';
import IOElement from 'shared/components/IOElement';

interface RenderIfVisibleProps {
  initialVisible?: boolean;
  defaultHeight?: number;
  visibleOffsetTop?: number;
  visibleOffesetBottom?: number;
  stayRendered?: boolean;
  root?: HTMLElement | null;
  RootElement?: ElementType;
  PlaceholderElement?: ElementType;
  placeholderElementClass?: string;
  children: React.ReactNode;
  className?: string;
}

const RenderIfVisible = ({
  initialVisible = false,
  defaultHeight = 0,
  visibleOffsetTop = 1000,
  visibleOffesetBottom = 1000,
  stayRendered = false,
  root = null,
  RootElement = 'div',
  PlaceholderElement = 'div',
  placeholderElementClass = '',
  children,
  className = '',
}: RenderIfVisibleProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(initialVisible);
  const placeholderHeight = useRef<number>(defaultHeight);
  const intersectionRef = useRef<HTMLDivElement>(null);
  let idleCallbackHandle: number | null = null;

  useEffect(() => {
    return () => {
      idleCallbackHandle && window.cancelIdleCallback(idleCallbackHandle);
    };
  });

  const onVisible = () => {
    // setiing height only whenever the content is made visible
    placeholderHeight.current = intersectionRef.current?.offsetHeight || defaultHeight;

    if (typeof window !== undefined && window.requestIdleCallback) {
      idleCallbackHandle = window.requestIdleCallback(() => setIsVisible(true), {
        timeout: 500,
      });
    } else {
      setIsVisible(true);
    }
  };

  const onHide = () => {
    if (!stayRendered) setIsVisible(false);
  };

  const placeholderStyle = { height: placeholderHeight.current };

  return (
    <IOElement
      as={RootElement}
      ref={intersectionRef}
      className={className}
      onVisible={onVisible}
      options={{ root, rootMargin: `${visibleOffsetTop}px 0px ${visibleOffesetBottom}px 0px` }}
      onHidden={onHide}
    >
      {isVisible ? (
        children
      ) : (
        <PlaceholderElement
          className={placeholderElementClass}
          style={placeholderStyle}
        ></PlaceholderElement>
      )}
    </IOElement>
  );
};

export default RenderIfVisible;
